/**
 * One child's privacy, in Settings: permission, pause, and what we hold.
 *
 * The three parent rights COPPA gives (16 CFR 312.6), each one button:
 *
 *   review     "See what Birbly holds" -- the server's own list of every
 *              record about this child (family/consent.ts `childData`)
 *   refuse     "Withdraw permission" stops all collection from this child at
 *              once; "Pause" is the softer switch, for a while
 *   delete     removing the child, from the Family tab, deletes their
 *              information (the sentence below says where)
 *
 * The pause existed before consent did (2026-10-04) and stays separate: a
 * parent can pause a child they have given permission for without withdrawing
 * it, and withdrawing does not need a pause.
 */

import { useCallback, useEffect, useState } from 'react';
import { ApiError } from '../api/client';
import { Layer } from '../brand/Layer';
import { Loading } from '../components/Spinner';
import {
  childData,
  consentStatus,
  setPaused,
  withdrawConsent,
  type ChildDataReport,
  type ConsentStatus,
} from '../family/consent';
import { ConsentSheet } from './ConsentSheet';

const STATE_WORDS: Record<ConsentStatus['state'], string> = {
  active: 'Permission given',
  pending: 'Waiting for you to open the email',
  revoked: 'Permission withdrawn',
  'renewal-required': 'Our notice changed — please confirm again',
  none: 'Permission not given yet',
};

export function ChildPrivacy({
  familyId,
  personId,
  name = '',
}: {
  familyId: string;
  personId: string;
  name?: string;
}) {
  const who = name || 'this child';
  const [status, setStatus] = useState<ConsentStatus | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [attempt, setAttempt] = useState(0);
  const [asking, setAsking] = useState(false);
  const [confirmingWithdraw, setConfirmingWithdraw] = useState(false);
  const [report, setReport] = useState<ChildDataReport | 'loading' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoadError('');
    void consentStatus(familyId, personId)
      .then((result) => {
        if (!cancelled) setStatus(result);
      })
      .catch(() => {
        if (!cancelled) {
          setStatus(null);
          setLoadError('Child privacy settings could not be loaded.');
        }
      });
    return () => {
      cancelled = true;
    };
  }, [familyId, personId, attempt]);

  const run = useCallback(async (work: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await work();
      setAttempt((value) => value + 1);
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'The change could not be confirmed. Check the setting before trying again.');
      // A lost response may have followed a successful write: re-read what
      // the server holds rather than showing what we hoped it holds.
      setStatus(null);
      setAttempt((value) => value + 1);
    } finally {
      setBusy(false);
    }
  }, []);

  async function review() {
    setReport('loading');
    try {
      setReport(await childData(familyId, personId));
    } catch (problem) {
      setReport(null);
      setError(problem instanceof ApiError ? problem.message : 'That could not be loaded.');
    }
  }

  if (!status) {
    return (
      <div className="child-privacy">
        <p className="field-label">Privacy</p>
        {error ? <p role="alert" className="error">{error}</p> : null}
        {loadError ? (
          <>
            <p role="alert" className="error">{loadError}</p>
            <button type="button" onClick={() => setAttempt((value) => value + 1)}>Check again</button>
          </>
        ) : (
          <Loading label="Loading privacy settings…" />
        )}
      </div>
    );
  }

  // Always shown: consent is asked for and recorded even before the server
  // enforces it (ConsentRequired), so families can answer ahead of the switch.
  const consentMatters = true;
  const canAsk = status.state !== 'active';

  return (
    <div className="child-privacy">
      <p className="field-label">Privacy</p>

      {consentMatters ? (
        <div className="privacy-row">
          <div>
            <p className="privacy-state" role="status">{STATE_WORDS[status.state]}</p>
            <p className="footnote">
              {status.state === 'active' && status.consentAt
                ? `Given ${status.consentAt.slice(0, 10)} by email confirmation.`
                : status.state === 'revoked'
                  ? `${who} cannot use their page, design a character or change goals until you give it again.`
                  : status.required
                    ? `${who} cannot use their own page until you do.`
                    : `Soon ${who} will need this to keep using their own page.`}
            </p>
          </div>
          {canAsk ? (
            <button type="button" className="primary" disabled={busy} onClick={() => setAsking(true)}>
              {status.state === 'pending' ? 'Send again' : 'Give permission'}
            </button>
          ) : (
            <button type="button" className="text-button danger" disabled={busy} onClick={() => setConfirmingWithdraw(true)}>
              Withdraw permission
            </button>
          )}
        </div>
      ) : null}

      <div className="privacy-row">
        <div>
          <p className="privacy-state">{status.paused ? 'Child access is paused' : 'Child access is on'}</p>
          <p className="footnote">
            Pausing stops their page, new character suggestions and goal changes for now. Parents
            can still manage everything, and nothing is deleted.
          </p>
        </div>
        <button aria-busy={busy} type="button" disabled={busy} onClick={() => void run(() => setPaused(familyId, personId, !status.paused))}>
          {status.paused ? 'Resume' : 'Pause'}
        </button>
      </div>

      <div className="privacy-row">
        <div>
          <p className="privacy-state">What Birbly holds about {who}</p>
          <p className="footnote">
            Every record about them, as our server sees it. To delete it all, remove {who} from
            the Family tab.
          </p>
        </div>
        <button type="button" className="text-button" disabled={busy || report === 'loading'} onClick={() => void review()}>
          {report === 'loading' ? 'Loading…' : 'See it'}
        </button>
      </div>

      {error ? <p role="alert" className="error">{error}</p> : null}

      {asking ? (
        <ConsentSheet
          familyId={familyId}
          personId={personId}
          name={name}
          renewal={status.state === 'renewal-required'}
          onClose={() => {
            setAsking(false);
            setAttempt((value) => value + 1);
          }}
        />
      ) : null}

      {confirmingWithdraw ? (
        <Layer onClose={() => setConfirmingWithdraw(false)} busy={busy}>
          <div className="dialog-layer" onClick={(event) => event.target === event.currentTarget && !busy && setConfirmingWithdraw(false)}>
            <div className="dialog" role="alertdialog" aria-modal="true" aria-labelledby={`withdraw-${personId}`}>
              <h3 id={`withdraw-${personId}`}>Withdraw permission for {who}?</h3>
              <p>
                Birbly stops collecting anything from {who} straight away: their devices stop
                working, and they cannot design a character or change goals. What is already
                saved stays until you remove them. You can give permission again later.
              </p>
              <div className="actions">
                <button
                  type="button"
                  className="danger"
                  aria-busy={busy}
                  disabled={busy}
                  onClick={() => void run(() => withdrawConsent(familyId, personId)).then(() => setConfirmingWithdraw(false))}
                >
                  {busy ? 'Withdrawing…' : 'Withdraw'}
                </button>
                <button type="button" disabled={busy} onClick={() => setConfirmingWithdraw(false)}>
                  Keep it
                </button>
              </div>
            </div>
          </div>
        </Layer>
      ) : null}

      {report && report !== 'loading' ? (
        <DataReport report={report} who={who} onClose={() => setReport(null)} />
      ) : null}
    </div>
  );
}

function sealed(value: { bytes: number } | null | undefined): string {
  return value ? `Encrypted (${value.bytes} bytes) — only your family’s devices can open it` : 'None';
}

/**
 * The report as a file the parent keeps (312.6: review, and keep). The share
 * sheet where there is one -- on a phone, and inside the iOS app, that is how
 * a file leaves -- else an ordinary download; and in a web view that can do
 * neither, onto the clipboard, so the button always does something.
 */
async function saveReport(report: ChildDataReport, who: string): Promise<string> {
  const text = JSON.stringify(report, null, 2);
  const name = `birbly-${(who || 'child').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-data.json`;
  const file = typeof File === 'function' ? new File([text], name, { type: 'application/json' }) : null;
  const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
  if (file && nav.share && nav.canShare?.({ files: [file] })) {
    try {
      await nav.share({ files: [file], title: `What Birbly holds about ${who}` });
      return '';
    } catch {
      // Dismissed, or refused: fall through to the download.
    }
  }
  const native = (window as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor?.isNativePlatform?.();
  if (native && navigator.clipboard) {
    await navigator.clipboard.writeText(text);
    return 'Copied. Paste it into a note or an email to keep it.';
  }
  const url = URL.createObjectURL(new Blob([text], { type: 'application/json' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return 'Saved to your downloads.';
}

function DataReport({ report, who, onClose }: { report: ChildDataReport; who: string; onClose: () => void }) {
  const [saved, setSaved] = useState('');
  const p = report.person;
  const rows: Array<[string, string]> = [
    ['Their id in your family', p.id],
    ['Added', p.created ? p.created.slice(0, 10) : '—'],
    ['Character and nickname', sealed(p.character)],
    ['Character waiting for you', p.draftWaiting ? `${sealed(p.draftWaiting)}, sent ${p.draftSent?.slice(0, 10) ?? ''}` : 'None'],
    ['Earlier character kept for undo', sealed(p.previousCharacter)],
    ['Their code', p.childCode ?? 'None'],
    ['Money rows', `${report.moneyRows.count}. ${report.moneyRows.note}`],
    ['Permission', `${STATE_WORDS[report.consent.state]}${report.consent.consentAt ? ` (${report.consent.consentAt.slice(0, 10)})` : ''}`],
  ];
  return (
    <Layer onClose={onClose}>
      <div className="sheet-layer" onClick={(event) => event.target === event.currentTarget && onClose()}>
        <div className="sheet data-report" role="dialog" aria-modal="true" aria-labelledby="data-report-title">
          <span className="sheet-handle" aria-hidden="true" />
          <h3 className="sheet-title" id="data-report-title">What Birbly holds about {who}</h3>
          <dl className="data-report-list">
            {rows.map(([term, value]) => (
              <div key={term}>
                <dt>{term}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          <h4 className="consent-heading">Devices</h4>
          {report.devices.length ? (
            <ul className="consent-list">
              {report.devices.map((device) => (
                <li key={device.id}>
                  {device.kind || 'A device'} — {device.revoked ? 'unlinked' : device.status}
                  {device.approvedAt ? `, approved ${device.approvedAt.slice(0, 10)}` : ''}
                  {device.lastSeenDate ? `, last used ${device.lastSeenDate}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="consent-small">None.</p>
          )}
          <h4 className="consent-heading">Goals they are part of</h4>
          <p className="consent-small">
            {report.goals.length
              ? `${report.goals.length} (names encrypted). Their money in them is shown on the Goals tab.`
              : 'None.'}
          </p>
          <h4 className="consent-heading">Never held</h4>
          <p className="consent-small">{report.notHeld.join(', ')}.</p>
          <div className="sheet-actions">
            <button type="button" onClick={() => void saveReport(report, who).then(setSaved).catch(() => setSaved('That did not save. Try again.'))}>Download a copy</button>
            {saved ? <p role="status" className="consent-small">{saved}</p> : null}
            <button type="button" className="primary full" onClick={onClose} data-autofocus>Done</button>
          </div>
        </div>
      </div>
    </Layer>
  );
}
